import React, { useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { useRaceStore } from '../../stores/raceStore';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
function MapUpdater({
  points
}: {
  points: [number, number][];
}) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, {
        padding: [50, 50]
      });
    }
  }, [points, map]);
  return null;
}
interface CourseMapProps {
  hoverPoint?: {
    lat: number;
    lon: number;
  } | null;
  onRouteClick?: (km: number) => void;
}
export function CourseMap({
  hoverPoint,
  onRouteClick
}: CourseMapProps) {
  const {
    gpxData,
    segments
  } = useRaceStore();
  const findClosestKm = useCallback((lat: number, lon: number): number | null => {
    if (!gpxData || gpxData.points.length === 0) return null;
    let minDist = Infinity;
    let closestIdx = 0;
    for (let i = 0; i < gpxData.points.length; i++) {
      const p = gpxData.points[i];
      const dLat = p.lat - lat;
      const dLon = p.lon - lon;
      const dist = dLat * dLat + dLon * dLon;
      if (dist < minDist) {
        minDist = dist;
        closestIdx = i;
      }
    }
    return gpxData.points[closestIdx].distFromStart ?? 0;
  }, [gpxData]);
  if (!gpxData || gpxData.points.length === 0) return null;
  const routePoints: [number, number][] = gpxData.points.map(p => [p.lat, p.lon]);
  const splitMarkers = segments.slice(0, -1).map(seg => {
    const pt = gpxData.points[seg.endPointIndex];
    return {
      position: [pt.lat, pt.lon] as [number, number],
      name: seg.name,
      dist: pt.distFromStart?.toFixed(1),
      checkpointType: seg.checkpointType
    };
  });
  const CHECKPOINT_COLORS: Record<string, string> = {
    None: '#FC4C02',
    BV: '#0666EB',
    Aid: '#00B843',
    Water: '#38BDF8',
    Other: '#FF9500'
  };
  const handlePolylineClick = (e: L.LeafletMouseEvent) => {
    if (!onRouteClick) return;
    const {
      lat,
      lng
    } = e.latlng;
    const km = findClosestKm(lat, lng);
    if (km !== null && km > 0.5 && km < gpxData.totalDistance - 0.5) {
      onRouteClick(km);
    }
  };
  return <div className="absolute inset-0 rounded-lg overflow-hidden z-0" data-id="element-145">
      <MapContainer center={routePoints[0]} zoom={13} style={{
      height: '100%',
      width: '100%'
    }} scrollWheelZoom={false} data-id="element-146">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-147" />
        <Polyline positions={routePoints} color="transparent" weight={20} opacity={0} eventHandlers={{
        click: handlePolylineClick
      }} pathOptions={{
        className: 'cursor-pointer'
      }} data-id="element-148" />
        <Polyline positions={routePoints} color="#3b82f6" weight={4} opacity={0.8} eventHandlers={{
        click: handlePolylineClick
      }} pathOptions={{
        className: 'cursor-pointer'
      }} data-id="element-149" />
        {/* Start Marker */}
        <CircleMarker center={routePoints[0]} radius={8} pathOptions={{
        fillColor: '#00B843',
        fillOpacity: 1,
        color: '#fff',
        weight: 3
      }} data-id="element-150">
          <Popup data-id="element-151">
            <strong data-id="element-152">Start</strong>
          </Popup>
        </CircleMarker>
        {/* Split Markers */}
        {splitMarkers.map((marker, idx) => {
        const color = CHECKPOINT_COLORS[marker.checkpointType] || '#FC4C02';
        return <CircleMarker key={idx} center={marker.position} radius={6} pathOptions={{
          fillColor: color,
          fillOpacity: 1,
          color: '#fff',
          weight: 2
        }} data-id="element-153">
              <Popup data-id="element-154">
                <strong data-id="element-155">{marker.name}</strong>
                <br data-id="element-156" />
                {marker.dist} km
              </Popup>
            </CircleMarker>;
      })}
        {/* Finish Marker */}
        <CircleMarker center={routePoints[routePoints.length - 1]} radius={8} pathOptions={{
        fillColor: '#E34402',
        fillOpacity: 1,
        color: '#fff',
        weight: 3
      }} data-id="element-157">
          <Popup data-id="element-158">
            <strong data-id="element-159">Finish</strong>
          </Popup>
        </CircleMarker>
        {/* Hover cursor from elevation profile */}
        {hoverPoint && <CircleMarker center={[hoverPoint.lat, hoverPoint.lon]} radius={7} pathOptions={{
        fillColor: '#FC4C02',
        fillOpacity: 1,
        color: '#fff',
        weight: 3
      }} data-id="element-160" />}
        <MapUpdater points={routePoints} data-id="element-161" />
      </MapContainer>
    </div>;
}